import React, { createContext, useContext, useState, useEffect } from "react";
import type { User } from "@/types/user";
import { checkToken } from "@/api/auth";
import { useLocation } from "react-router-dom";
import { createMockUser } from "@/mocks/mockConfig";

interface AuthContextType {
  authenticated: boolean;
  setAuthenticated: (value: boolean) => void;
  user: User | null;
  setUser: (user: User | null) => void;
  token: string | null;
  setToken: (token: string | null) => void;
  isLoading: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  // Check for existing authentication on mount
  useEffect(() => {
    // Only run once on mount, not on every navigation
    const initAuth = async () => {
      try {
        // Check for noguard parameter
        const searchParams = new URLSearchParams(location.search);
        const isNoGuard = searchParams.has('noguard');
        
        if (isNoGuard) {
          // In noguard mode, set mock authenticated state
          const mockUser = createMockUser(location.pathname);
          setUser(mockUser);
          setAuthenticated(true);
          setToken('mock-token');
          setIsLoading(false);
          return;
        }
        
        // Normal authentication flow
        const tokenCheck = await checkToken();
        
        if (tokenCheck.isValid && tokenCheck.user) {
          setUser(tokenCheck.user);
          setAuthenticated(true);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Clear auth state on error
        setAuthenticated(false);
        setUser(null);
        setToken(null);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []); // Empty dependency array - only run once on mount

  // Separate effect for handling noguard mode pathname changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const isNoGuard = searchParams.has('noguard');
    
    if (isNoGuard && authenticated) {
      // Update mock user when pathname changes in noguard mode
      const mockUser = createMockUser(location.pathname);
      setUser(mockUser);
    }
  }, [location.pathname, location.search, authenticated]);

  // Logout function to clear all auth state
  const logout = () => {
    setAuthenticated(false);
    setUser(null);
    setToken(null);
  };

  return (
    <AuthContext.Provider value={{ 
      authenticated, 
      setAuthenticated, 
      user, 
      setUser, 
      token, 
      setToken,
      isLoading,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}